import { zodResolver } from '@hookform/resolvers/zod';
import { CircleAlert, UsersRound } from 'lucide-react';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { CountrySelect } from '@/components/forms/country-dropdown';
import { PasswordInput } from '@/components/forms/password-input';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/utils/cn';

import { useCreateTeam } from '../api/createTeam';

interface CreateTeamProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const formSchema = z.object({
  team_name: z
    .string()
    .min(3, 'Team name must be at least 3 characters')
    .max(50, 'Team name must be less than 50 characters')
    .regex(
      /^[a-zA-Z0-9 ]+$/,
      'Team name can only contain letters, numbers, and spaces',
    ),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  country: z.string({
    required_error: 'Please select a country',
  }),
});

export type CreateFormValues = z.infer<typeof formSchema>;

export const CreateTeam: React.FC<CreateTeamProps> = ({ open, setOpen }) => {
  const isMobile = useIsMobile();

  if (!isMobile) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <div className="mb-2 flex flex-col gap-2">
            <div
              className="flex size-11 shrink-0 items-center justify-center rounded-full border"
              aria-hidden="true"
            >
              <UsersRound className="text-primary" />
            </div>
            <DialogHeader>
              <DialogTitle>Create a team</DialogTitle>
              <DialogDescription>
                We just need a few details to get you started.
              </DialogDescription>
            </DialogHeader>
          </div>
          <CreateTeamForm setOpen={setOpen} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerContent>
        <DrawerHeader className="text-left">
          <DrawerTitle>Create a team</DrawerTitle>
          <DrawerDescription>
            We just need a few details to get you started.
          </DrawerDescription>
        </DrawerHeader>
        <CreateTeamForm className="px-4" setOpen={setOpen} />
        <DrawerFooter className="pt-2">
          <DrawerClose asChild>
            <Button variant="outline">Cancel</Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

interface CreateTeamFormProps extends React.ComponentProps<'form'> {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function CreateTeamForm({ className, setOpen }: CreateTeamFormProps) {
  const createTeamMutation = useCreateTeam();
  const form = useForm<CreateFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      team_name: '',
      password: '',
    },
    mode: 'onChange',
  });

  const onSubmit = async (values: CreateFormValues) => {
    createTeamMutation.mutate(
      { data: values },
      {
        onSuccess: () => {
          toast.success('Team created successfully!');
          setOpen(false);
        },
        onError: (error: any) => {
          if (error.data?.fields) {
            Object.entries(error.data.fields).forEach(([field, message]) => {
              form.setError(field as keyof CreateFormValues, {
                type: 'server',
                message: message as string,
              });
            });
          }
        },
      },
    );
  };

  const handleError = (errors: any) => {
    for (const key in errors) {
      toast.error(errors[key]?.message || `Error in ${key}`, {
        icon: <CircleAlert size={16} className="text-destructive" />,
      });
    }
  };

  const { isValid, isDirty } = form.formState;
  const isLoading = createTeamMutation.isPending;

  return (
    <Form {...form}>
      <form
        className={cn('grid items-start gap-4', className)}
        onSubmit={form.handleSubmit(onSubmit, handleError)}
      >
        <FormField
          control={form.control}
          name="team_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Fnatic Academy" {...field} />
              </FormControl>
              <FormDescription>This is your team display name.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <CountrySelect
          control={form.control}
          name="country"
          description="Where is your team based?"
        />
        <PasswordInput control={form.control} name="password" />
        <Button type="submit" disabled={isLoading || !isDirty || !isValid}>
          {isLoading ? 'Creating...' : 'Create'}
        </Button>
      </form>
    </Form>
  );
}
